/**
 * The presentational shell every state that carries a severity renders through.
 *
 * `DESIGN.md` §4 turns spec 01 §19's "không chỉ dùng màu để truyền đạt severity"
 * into three concrete channels — colour token, icon silhouette, text label — and
 * makes the **text label mandatory**. This component is where that becomes
 * structural rather than a rule people remember: there is no prop that renders the
 * colour without the icon and the label, so a screen cannot ship a colour-only
 * severity by accident.
 *
 * The severity word itself is rendered visually hidden when the note already has a
 * title that says the same thing in plainer words — hiding it from sight, never
 * from the accessibility tree. A sighted reader gets "Agent ngoại tuyến"; a screen
 * reader gets "Cao. Agent ngoại tuyến." Both get two channels; neither gets colour
 * alone.
 */

import type { ReactNode } from 'react';

import { useT } from '../i18n';
import type { MessageKey } from '../i18n/messages';
import { Icon, SEVERITY_ICON, type Severity } from './icons';

const SEVERITY_LABEL_KEY: Readonly<Record<Severity, MessageKey>> = {
  critical: 'severity.critical',
  high: 'severity.high',
  medium: 'severity.medium',
  low: 'severity.low',
  info: 'severity.info',
};

export interface StatusNoteProps {
  readonly severity: Severity;
  readonly title: string;
  readonly children?: ReactNode;
  readonly actions?: ReactNode;
  /**
   * `'status'` for a state that appeared as data arrived — polite, does not
   * interrupt. `'alert'` for one that appeared because something the person just
   * did failed. Defaults to `'status'`, because eight of the nine states describe
   * the data rather than the person's last action.
   */
  readonly role?: 'status' | 'alert';
  readonly testId?: string;
}

export function StatusNote({
  severity,
  title,
  children,
  actions,
  role = 'status',
  testId,
}: StatusNoteProps): ReactNode {
  const t = useT();
  return (
    <div
      className={`wp-note wp-note--${severity}`}
      role={role}
      data-severity={severity}
      {...(testId === undefined ? {} : { 'data-testid': testId })}
    >
      <Icon name={SEVERITY_ICON[severity]} className="wp-note__icon" />
      <div className="wp-note__body">
        <p className="wp-note__label">
          <span className="wp-visually-hidden">{t(SEVERITY_LABEL_KEY[severity])}. </span>
          {title}
        </p>
        {children}
        {actions === undefined ? null : <div className="wp-note__actions">{actions}</div>}
      </div>
    </div>
  );
}
